import { useState, useCallback, useEffect, useRef } from "react";
import {
  createFileRoute,
  useRouterState,
  useNavigate,
} from "@tanstack/react-router";
import { ArrowLineDown } from "@phosphor-icons/react";
import { Button } from "@repo/ui";
import { ReceiptHistoryTable } from "@/components/receipts/receipt-history-table";
import { ReceiptDetailView } from "@/components/receipts/receipt-detail-view";
import { ReceiptDeleteDialog } from "@/components/receipts/receipt-delete-dialog";
import { ReceiptFormPanel } from "@/components/receipts/receipt-form-panel";
import { NotesThread } from "@/components/notes-thread";
import { PermissionGate } from "@/components/security";
import { createReceipt, updateReceipt, getReceipt } from "@/lib/api/receipts";
import { showErrorToast, showSuccessToast } from "@/lib/utils/error-display";
import { ApiClientError } from "@/lib/api/client";
import type { Receipt, ReceiptFormData } from "@/lib/types/inventory";

export const Route = createFileRoute("/_app/receipts")({
  component: ReceiptsPage,
});

function ReceiptsPage() {
  const navigate = useNavigate();
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const searchParams = new URLSearchParams(searchStr);
  const highlightId = searchParams.get("id");

  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [viewMode, setViewMode] = useState<"view" | "notes" | null>(null);
  const [receiptToDelete, setReceiptToDelete] = useState<Receipt | null>(null);
  const [receiptToEdit, setReceiptToEdit] = useState<Receipt | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [formOpen, setFormOpen] = useState(false);

  // Auto-open detail view when navigating with ?id= param
  const didOpenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightId || didOpenRef.current === highlightId) return;
    didOpenRef.current = highlightId;

    getReceipt(highlightId)
      .then((result: unknown) => {
        // API returns { success, data: { receipt: {...} } }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res = result as any;
        const receipt = res?.data?.receipt ?? res?.receipt ?? res?.data ?? res;
        if (receipt?.id) {
          setSelectedReceipt(receipt as Receipt);
          setViewMode("view");
        }
      })
      .catch(() => {
        // Record not found — clear the param
        navigate({ to: "/receipts", replace: true });
      });
  }, [highlightId, navigate]);

  // Lean handlers — approval modal and retry are owned by the drawer.
  // We only pass the token through (when the drawer has one) and
  // re-throw everything so the drawer can decide what to do.
  const handleCreateReceiptRow = useCallback(
    async (data: ReceiptFormData, approvalToken?: string) => {
      const payload = {
        ...(data as Record<string, unknown>),
        ...(approvalToken ? { approvalToken } : {}),
      };
      try {
        const result = (await createReceipt(payload)) as {
          data?: { receipt?: { id?: string } };
        };
        return result?.data?.receipt?.id;
      } catch (error) {
        if (error instanceof ApiClientError && error.statusCode === 403) {
          throw error;
        }
        showErrorToast(error, "Create receipt");
        throw error;
      }
    },
    []
  );

  const handleUpdateReceipt = useCallback(
    async (data: ReceiptFormData, approvalToken?: string) => {
      if (!receiptToEdit) return;
      const payload = {
        ...(data as Record<string, unknown>),
        ...(approvalToken ? { approvalToken } : {}),
      };
      try {
        await updateReceipt(receiptToEdit.id, payload);
        showSuccessToast(
          `Successfully updated receipt for ${data.ndc}`,
          "Receipt Updated"
        );
        setReceiptToEdit(null);
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        if (error instanceof ApiClientError && error.statusCode === 403) {
          throw error;
        }
        showErrorToast(error, "Update receipt");
        throw error;
      }
    },
    [receiptToEdit]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Receipts</h1>
        <PermissionGate permission="receipts:create">
          <Button
            size="sm"
            className="gap-1.5 h-9"
            onClick={() => setFormOpen(true)}
            data-testid="receipt-create"
          >
            <ArrowLineDown weight="bold" size={15} />
            Log Receipt
          </Button>
        </PermissionGate>
      </div>

      <ReceiptHistoryTable
        onView={(receipt) => {
          setSelectedReceipt(receipt);
          setViewMode("view");
        }}
        onEdit={(receipt) => setReceiptToEdit(receipt)}
        onDelete={(receipt) => setReceiptToDelete(receipt)}
        onEditNotes={(receipt) => {
          setSelectedReceipt(receipt);
          setViewMode("notes");
        }}
        refreshTrigger={refreshTrigger}
      />

      <ReceiptDetailView
        receipt={viewMode === "view" ? selectedReceipt : null}
        open={viewMode === "view" && !!selectedReceipt}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedReceipt(null);
            setViewMode(null);
            // Clear ?id= param if present so re-opening works
            if (highlightId) {
              navigate({ to: "/receipts", replace: true });
            }
          }
        }}
      />

      <NotesThread
        open={viewMode === "notes" && !!selectedReceipt}
        onClose={() => {
          setSelectedReceipt(null);
          setViewMode(null);
        }}
        entityType="receipt"
        entityId={selectedReceipt?.id ?? ""}
        ndc={selectedReceipt?.ndc ?? ""}
        itemName={selectedReceipt?.inventory?.item_name ?? ""}
      />

      <ReceiptDeleteDialog
        receipt={receiptToDelete}
        open={!!receiptToDelete}
        onOpenChange={(open) => {
          if (!open) setReceiptToDelete(null);
        }}
        onSuccess={() => {
          setReceiptToDelete(null);
          setRefreshTrigger((prev) => prev + 1);
        }}
      />

      <ReceiptFormPanel
        open={formOpen}
        onOpenChange={setFormOpen}
        mode="create"
        onSubmitRow={handleCreateReceiptRow}
        onAllSubmitted={() => {
          setRefreshTrigger((prev) => prev + 1);
        }}
      />

      <ReceiptFormPanel
        open={!!receiptToEdit}
        onOpenChange={(open) => {
          if (!open) setReceiptToEdit(null);
        }}
        mode="edit"
        onUpdate={handleUpdateReceipt}
        initialData={
          receiptToEdit
            ? {
                dateReceived: new Date(
                  receiptToEdit.date_received + "T00:00:00"
                ),
                ndc: receiptToEdit.ndc,
                itemName: receiptToEdit.inventory?.item_name ?? "",
                quantityReceived: receiptToEdit.quantity_received,
                wholesalerId: receiptToEdit.wholesaler_id ?? "",
                invoiceNumber: receiptToEdit.invoice_number,
              }
            : undefined
        }
      />
    </div>
  );
}
